import { WbCard, WbGridCol, WbGridRow, WbHeading, WbIcon, WbLink, WbListGroupItem } from '@workbench/react';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { blue } from '@mercedes-benz/mui5-theme/build/main/lib/Colors';
import { useTranslation } from '../../common/i18n/useTranslation';
import { useRestOperations } from '../../common/rest/restOperations';
import { ApiUrls } from '../../../api/api/apiUrls';
import { useEffect, useState } from 'react';
import { useTranslationContext } from '../../common/i18n/TranslationProvider';
import { SupportedLanguages } from '../../common/i18n/SupportedLanguages';
import { Routes } from '../../routes/routes';
import { useNavigate } from 'react-router-dom';
import { FetchedFileWithTags } from '../../utils/interfaces';
import BeamButton from '../../common/components/input/BeamButton';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const useStyles = makeStyles()((theme: Theme) => ({
    cardTitleColor: {
        color: blue[50],
    },
    card: {
        padding: theme.spacing(4),
        marginTop: theme.spacing(2),
    },
    dateItem: {
        paddingLeft: 0,
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
    },
    icon: {
        marginRight: theme.spacing(1),
    },
}));

interface NewsCardProps {
    title?: string;
    date?: string;
    newsId?: string;
}

function NewsCard(props: Readonly<NewsCardProps>) {
    const { classes } = useStyles();
    const translate = useTranslation();
    const { doGetRequest } = useRestOperations<FetchedFileWithTags>();
    const [files, setFiles] = useState<FetchedFileWithTags[]>([]);
    const { langState } = useTranslationContext();
    const navigate = useNavigate();
    const { doDownloadFile } = useRestOperations();

    const fetchFiles = async () => {
        if (!props.newsId) {
            return;
        }

        const result = await doGetRequest({
            queryString: ApiUrls.userApi.getInfoAboutNewsFileById(props.newsId),
        });

        if (result) {
            const files = result as FetchedFileWithTags[];
            setFiles(files);
        }
    };

    const downloadNewsFile = async () => {
        if (!props.newsId) {
            return;
        }

        const fileInfoByLanguage = getFileInfoBylanguage();

        if (!fileInfoByLanguage) {
            return;
        }

        const fileName = fileInfoByLanguage.originalName;
        await doDownloadFile(ApiUrls.userApi.downloadNewsFileById(props.newsId, fileInfoByLanguage.id), fileName);
    };

    const getFileInfoBylanguage = (): FetchedFileWithTags | undefined => {
        return files.find((fileContent) =>
            langState === SupportedLanguages.de
                ? fileContent.tags.language === 'DE'
                : fileContent.tags.language === 'EN'
        );
    };

    useEffect(() => {
        fetchFiles();
    }, []);

    const handlelickDownload = async () => {
        await downloadNewsFile();
    };

    const fileIsAvailable = (): boolean =>
        files.length > 0 &&
        (getFileInfoBylanguage()?.tags.language === 'DE' || getFileInfoBylanguage()?.tags.language === 'EN');

    return (
        <WbCard className={classes.card}>
            <WbGridRow>
                <WbGridCol mq1={12}>
                    <WbLink
                        variant="inline"
                        onClick={async () => {
                            await navigate(`${Routes.news}/${props.newsId}`);
                        }}
                    >
                        <WbHeading size="m" className={classes.cardTitleColor}>
                            {props.title}
                        </WbHeading>
                    </WbLink>
                </WbGridCol>
            </WbGridRow>
            <WbGridRow>
                <WbGridCol mq1={12}>
                    <WbListGroupItem className={classes.dateItem}>
                        <span slot="icon"> {<CalendarTodayIcon className={classes.icon} />}</span>
                        {props.date}
                    </WbListGroupItem>
                </WbGridCol>
            </WbGridRow>
            {fileIsAvailable() && (
                <WbGridRow>
                    <WbGridCol mq1={12}>
                        <BeamButton
                            className={classes.dateItem}
                            size="m"
                            variant="primary"
                            onClick={handlelickDownload}
                        >
                            <WbIcon slot="icon" name="bds/download-print/24" icon-flip-rtl />
                            {translate('download_PDF')}
                        </BeamButton>
                    </WbGridCol>
                </WbGridRow>
            )}
        </WbCard>
    );
}

export default NewsCard;
